import React, { useState, useEffect } from 'react';
import { navigationGroups } from '../../lib/navigation';
import { Brain, Cloud, CloudOff } from 'lucide-react';
import { useStore } from '../../store/useStore';

interface HorizontalNavProps {
  activeModule: string;
  setActiveModule: (id: string) => void;
}

export function HorizontalNav({ activeModule, setActiveModule }: HorizontalNavProps) {
  const activeGroup = navigationGroups.find(g => g.items.some(i => i.id === activeModule)) || navigationGroups[0];
  
  // Calculate progress heuristically:
  const store = useStore();
  const [progress, setProgress] = useState(0);
  const [saveState, setSaveState] = useState<'saved' | 'saving'>('saved');

  // Any time store changes, trigger fake auto-save
  useEffect(() => {
     setSaveState('saving');
     const t = setTimeout(() => {
        setSaveState('saved');
     }, 1000);
     return () => clearTimeout(t);
  }, [store]);

  // Dummy logic: calculate progress based on populated fields
  useEffect(() => {
     let filled = 0;
     let total = 0;
     const cnt = (val: any) => { total++; if (val && (typeof val !== 'object' || Object.keys(val).length > 0)) filled++; };
     
     cnt(store.companyData.razaoSocial); cnt(store.companyData.whatsappPrincipal);
     cnt(store.businessDetails.oQueFaz); cnt(store.businessDetails.missao);
     cnt(store.idealClient.faixaEtaria); cnt(store.idealClient.dores.length > 0);
     cnt(store.salesProcess.comoEntra);
     cnt(store.connections.metaConnected); cnt(store.connections.whatsappConnected);
     cnt(store.objectives.meses3Faturamento);
     cnt(store.knowledgeBase.conteudoMassivo);
     
     const val = Math.round((filled / total) * 100) || 5;
     setProgress(Math.max(5, val));
  }, [store]);

  return (
    <div className="bg-transparent mb-6">
      
      {/* Top utility bar */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
         <div className="flex items-center gap-4">
             <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-600 text-white shadow-sm">
                <Brain size={20} />
             </div>
             <div>
                <h1 className="font-bold text-gray-900 leading-tight">Cérebro IA</h1>
                <div className="flex items-center gap-2 mt-1">
                   <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-600 transition-all duration-1000" style={{ width: `${progress}%` }} />
                   </div>
                   <span className="text-xs font-semibold text-gray-500">{progress}% completo</span>
                </div>
             </div>
         </div>
         
         <div className="flex items-center gap-2 text-sm">
            {saveState === 'saved' ? (
                <div className="flex items-center gap-1.5 text-green-600 bg-green-50 px-3 py-1.5 rounded-lg border border-green-100">
                   <Cloud size={16} />
                   <span className="font-medium">Salvo na nuvem</span>
                </div>
            ) : (
                <div className="flex items-center gap-1.5 text-gray-500 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm">
                   <CloudOff size={16} className="animate-pulse" />
                   <span className="font-medium">Salvando...</span>
                </div>
            )}
         </div>
      </div>

      <div className="flex items-center gap-6 overflow-x-auto custom-scrollbar pb-2 mb-4 border-b border-gray-200">
        {navigationGroups.map((group) => {
          const isActiveGroup = activeGroup.title === group.title;
          return (
            <button
              key={group.title}
              onClick={() => setActiveModule(group.items[0].id)}
              className={`whitespace-nowrap py-3 font-medium text-sm transition-colors relative border-b-2 ${
                isActiveGroup 
                  ? 'text-gray-900 border-gray-900'
                  : 'text-gray-500 border-transparent hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              {group.title}
            </button>
          )
        })}
      </div>
      
      <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-2">
        {activeGroup.items.map((item) => {
          const Icon = item.icon;
          const isActive = activeModule === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveModule(item.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200 ring-1 ring-inset ring-gray-900/5'
                  : 'text-gray-500 hover:bg-white hover:text-gray-900 border border-transparent'
              }`}
            >
              <Icon size={16} className={isActive ? 'text-gray-900' : 'text-gray-400'} />
              {item.title}
            </button>
          )
        })}
      </div>
    </div>
  );
}
